import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Chrome, Eye, EyeOff, Lock, Mail, Phone, User } from "lucide-react";
import { createEmailAccount, signInWithGoogle, updateAuthDisplayName } from "../services/authService";
import { upsertUserProfile } from "../services/profileService";
import { PageShell } from "../components/PageShell";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import SafeImage from "../components/SafeImage";

const HERO_SRC = "/Images/hero-farm.jpg";

export default function SignupPage() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  async function onGoogle() {
    setBusy(true);
    try {
      const result = await signInWithGoogle();
      const u = result.user;
      const name = u.displayName || "";
      await upsertUserProfile(u.uid, {
        fullName: name,
        displayName: name,
        email: u.email || "",
        phone: u.phoneNumber || "",
      });
      toast.success("Account ready.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Google sign-up failed", { description: err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  }

  async function onSignup(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const cred = await createEmailAccount(email, password);
      await updateAuthDisplayName(cred.user, fullName.trim());
      await upsertUserProfile(cred.user.uid, {
        fullName: fullName.trim(),
        displayName: fullName.trim(),
        email,
        phone: phone.trim(),
      });
      toast.success("Welcome.");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Sign up failed", { description: err?.message || "Try again." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageShell>
      <section className="px-4 sm:px-6 pt-2 sm:pt-4 pb-16">
        <div className="mx-auto max-w-6xl grid lg:grid-cols-2 gap-10 items-stretch">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-card shadow-elevated">
            <div className="absolute inset-0">
              <SafeImage src={HERO_SRC} alt="" className="h-full w-full object-cover" fallbackLabel="Smart Farming" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(0.7_0.18_50/0.18),transparent_55%)]" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/25 to-transparent" />
            </div>
            <div className="relative z-10 p-10 h-full flex flex-col justify-end text-cream">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/20 px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-cream/80">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                Smart Farming
              </div>
              <h1 className="mt-6 font-display text-5xl text-balance">
                Create an <span className="italic text-accent">account.</span>
              </h1>
              <p className="mt-4 text-sm text-cream/80 max-w-md leading-relaxed">
                Then add a device id that has already been registered. Readings show up when that board is online.
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-card shadow-soft p-8 sm:p-10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-display text-3xl">Create account</div>
                <p className="mt-2 text-sm text-muted-foreground">Use Google or email/password.</p>
              </div>
              <Button variant="outline" onClick={onGoogle} type="button" disabled={busy}>
                <Chrome className="h-4 w-4" />
                Google
              </Button>
            </div>

            <form className="mt-6 space-y-4" onSubmit={onSignup}>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Full name</span>
                  <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <input
                      className="w-full bg-transparent outline-none text-sm"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Full name"
                      required
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Phone</span>
                  <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <input
                      className="w-full bg-transparent outline-none text-sm"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91XXXXXXXXXX"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Email</span>
                  <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <input
                      className="w-full bg-transparent outline-none text-sm"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      autoComplete="email"
                    />
                  </div>
                </label>

                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Password</span>
                  <div className="mt-2 flex items-center gap-2 rounded-2xl border border-input bg-cream/50 px-4 py-3">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <input
                      className="w-full bg-transparent outline-none text-sm"
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass((s) => !s)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPass ? "Hide password" : "Show password"}
                    >
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </label>

                <Button size="lg" className="w-full" disabled={busy} type="submit">
                  Create account
                </Button>

                <div className="text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <Link className="text-accent underline-grow" to="/login">
                    Sign in
                  </Link>
                </div>
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
